import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { imageStorage } from '@/store/idbStorage/imageStorage';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ZoomIn } from 'lucide-react';
import { Image } from '@/types/Image.type';
import { ImageUploadPendingIcon, FailedUploadImageIcon } from '@/assets/icons/svgs';
import {
    ImageZoomLightbox,
    ImageZoomSlide,
    InspectionImageCategoryGroup,
} from './ImageZoomLightbox';
import styles from './ImageCard.module.scss';

interface ImageCardProps {
    type: InspectionImageType;
    inspectionId: string;
    image: Image;
    onDelete: (img: Image) => void;
    /** Images shown in the lightbox carousel (typically every image in the module, in UI order) */
    galleryPeers?: Image[];
    /** Index of this card within `galleryPeers`; defaults to matching `image.id` */
    galleryIndex?: number;
    /** Section label when an image has no `imageType` string (e.g. Images2 group title) */
    sectionTypeLabel?: string;
    /** Grouped images for resolving each slide’s type label in the viewer */
    allInspectionImageGroups?: InspectionImageCategoryGroup[];
}

export const ImageCard: React.FC<ImageCardProps> = ({
    type,
    inspectionId,
    image,
    onDelete,
    galleryPeers,
    galleryIndex,
    sectionTypeLabel,
    allInspectionImageGroups,
}: ImageCardProps) => {
    const { t } = useTranslation();
    const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
    const [zoomOpen, setZoomOpen] = useState(false);

    const slidesForLightbox: ImageZoomSlide[] = useMemo(() => {
        const peers = galleryPeers ?? [image];
        return peers.map((img) => {
            let typeLabel =
                typeof img.imageType === 'string' && img.imageType.trim() ? img.imageType : '';
            if (!typeLabel && allInspectionImageGroups?.length) {
                const group = allInspectionImageGroups.find((g) =>
                    g.images.some((i) => i.id === img.id),
                );
                typeLabel = group?.typeLabel ?? '';
            }
            if (!typeLabel) typeLabel = sectionTypeLabel?.trim() || t('Images');
            return { image: img, typeLabel };
        });
    }, [galleryPeers, image, sectionTypeLabel, allInspectionImageGroups, t]);

    const initialGalleryIndex = useMemo(() => {
        const len = slidesForLightbox.length;
        const last = Math.max(0, len - 1);
        if (galleryIndex !== undefined) {
            return Math.min(Math.max(0, galleryIndex), last);
        }
        const found = slidesForLightbox.findIndex((s) => s.image.id === image.id);
        return found >= 0 ? Math.min(found, last) : 0;
    }, [galleryIndex, image.id, slidesForLightbox]);

    const hasRemoteUrl = Boolean(image.url?.trim() || image.uploadedUrl?.trim());

    const getStatusIcon = () => {
        if (hasRemoteUrl) {
            return '';
        }

        if (image.failed) {
            return <FailedUploadImageIcon />;
        }

        return <ImageUploadPendingIcon />;
    };
    const getImageStatusClass = () => {
        if (hasRemoteUrl) {
            return '';
        }

        if (image.failed) {
            return styles.failed;
        }

        return '';
    };
    useEffect(() => {
        const fromUrl = image.url?.trim();
        const fromUploaded = image.uploadedUrl?.trim();
        if (fromUrl) {
            setImageSrc(fromUrl);
        } else if (fromUploaded) {
            setImageSrc(fromUploaded);
        } else if (image?.data) {
            setImageSrc(image?.data);
        } else {
            imageStorage.getImage(inspectionId, type, image.id).then(setImageSrc);
        }
    }, [image?.id, image?.url, image?.uploadedUrl, image?.data, inspectionId, type]);

    const openZoom = useCallback(() => {
        if (imageSrc) setZoomOpen(true);
    }, [imageSrc]);

    const closeZoom = useCallback(() => setZoomOpen(false), []);

    const onKeyOpenZoom = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openZoom();
        }
    };

    return (
        <div className={styles.imageCardContainer}>
            <div
                className={`${styles.imageCard} ${getImageStatusClass()} ${imageSrc ? styles.zoomable : ''}`}
                role={imageSrc ? 'button' : undefined}
                tabIndex={imageSrc ? 0 : undefined}
                aria-label={imageSrc ? t('image_zoom_open', { defaultValue: 'View image larger' }) : undefined}
                onClick={(e) => {
                    e.stopPropagation();
                    openZoom();
                }}
                onKeyDown={imageSrc ? onKeyOpenZoom : undefined}
            >
                <img src={imageSrc} alt={`Uploaded ${image.id}`} className={styles.image} />
                {imageSrc && (
                    <span className={styles.zoomHintBadge} title={t('image_zoom_open')} aria-hidden>
                        <ZoomIn className={styles.zoomHintIcon} size={15} strokeWidth={2.25} />
                    </span>
                )}
            </div>
            <button
                className={styles.deleteButton}
                onClick={() => onDelete(image)}
            >
                &times;
            </button>
            <span className={styles.statusIcon}>
                {getStatusIcon()}
            </span>
            {zoomOpen && imageSrc && slidesForLightbox.length > 0 && (
                <ImageZoomLightbox
                    inspectionId={inspectionId}
                    inspectionImageType={type}
                    slides={slidesForLightbox}
                    initialIndex={initialGalleryIndex}
                    onClose={closeZoom}
                />
            )}
        </div>
    );
};
